import {
  ProviderProps,
  ReactElement,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types/commons';
import { IntegrationProvidersTypes } from '~globals/types/enums';
import { useSignalRContext } from '~contexts/SignalRContext';
import { Alert } from '~contexts/AlertsProvider';
import {
  AdminIntegration,
  AdminIntegrationRenovateData,
} from '~services/admin/integration/types';
import { useClientOnMethod } from '~hooks/useSignalR';
import { useToggleDialogConfirmation } from '~hooks/useToggleDialogConfirmation';
import { METHODS_NAME } from '~constants/signalR';
import { camelCaseSerializer } from '~utils/serializer';

import {
  IntegrationsSectionContextValue,
  IntegrationsSectionProps,
} from './types';

const IntegrationsSectionContext =
  createContext<IntegrationsSectionContextValue>({
    data: {
      list: [],
      loading: true,
      reload: () => {},
    },
    currentNewTab: {
      value: null,
      setValue: () => {},
    },
    dialogs: {
      add: {
        open: false,
        data: null,
        handleOpen: () => {},
        handleClose: () => {},
      },
      renovate: {
        open: false,
        data: null,
        handleOpen: () => {},
        handleClose: () => {},
      },
      delete: {
        open: false,
        data: null,
        handleOpen: () => {},
        handleClose: () => {},
      },
      assignWarehouses: {
        open: false,
        data: null,
        handleOpen: () => {},
        handleClose: () => {},
      },
      assignBillingList: {
        open: false,
        data: null,
        handleOpen: () => {},
        handleClose: () => {},
      },
    },
    hiddenTableEmpty: false,
  });

export const useIntegrationsSectionContext =
  (): IntegrationsSectionContextValue => useContext(IntegrationsSectionContext);

const IntegrationsSectionProvider = ({
  value: { list, loading, reload, hiddenTableEmpty = false },
  children,
}: ProviderProps<IntegrationsSectionProps>): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { hubConnection } = useSignalRContext();

  const [currentNewTab, setCurrentNewTab] = useState<Nullable<Window>>(null);

  const updateDataByChanges = useCallback(
    (notification: string) => {
      const alertInfo = camelCaseSerializer.serialize(
        JSON.parse(notification),
      ) as Alert;

      enqueueSnackbar(alertInfo.title, { variant: 'success' });

      reload();

      currentNewTab?.close();
      setTimeout(() => setCurrentNewTab(null), 500);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar, reload, currentNewTab],
  );

  useClientOnMethod(
    hubConnection,
    METHODS_NAME.REFRESH_INTEGRATION,
    updateDataByChanges,
  );

  const [
    openDialogAddIntegration,
    selectedAddIntegrationType,
    handleOpenDialogAddIntegration,
    handleCloseDialogAddIntegration,
  ] = useToggleDialogConfirmation<Nullable<IntegrationProvidersTypes>>(null);

  const [
    openDialogRenovateIntegration,
    selectedRenovateIntegrationData,
    handleOpenDialogRenovateIntegration,
    handleCloseDialogRenovateIntegration,
  ] = useToggleDialogConfirmation<Nullable<AdminIntegrationRenovateData>>(null);

  const [
    openDialogConfirmDeleteIntegration,
    selectedIntegrationDelete,
    handleOpenDialogConfirmDeleteIntegration,
    handleCloseDialogConfirmDeleteIntegration,
  ] = useToggleDialogConfirmation<Nullable<AdminIntegration>>(null);

  const [
    openDialogIntegrationAssignWarehouses,
    selectedCompanyIntegrationIdToAssignWarehouses,
    handleOpenDialogIntegrationAssignWarehouses,
    handleCloseDialogIntegrationAssignWarehouses,
  ] = useToggleDialogConfirmation<Nullable<string>>(null);

  const [
    openDialogAssignBillingList,
    selectedCompanyIntegrationToAssignBillingList,
    handleOpenDialogAssignBillingList,
    handleCloseDialogAssignBillingList,
  ] = useToggleDialogConfirmation<Nullable<AdminIntegration>>(null);

  return (
    <IntegrationsSectionContext.Provider
      value={{
        data: {
          list,
          loading,
          reload,
        },
        currentNewTab: {
          value: currentNewTab,
          setValue: setCurrentNewTab,
        },
        dialogs: {
          add: {
            open: openDialogAddIntegration,
            data: selectedAddIntegrationType,
            handleOpen: handleOpenDialogAddIntegration,
            handleClose: handleCloseDialogAddIntegration,
          },
          renovate: {
            open: openDialogRenovateIntegration,
            data: selectedRenovateIntegrationData,
            handleOpen: handleOpenDialogRenovateIntegration,
            handleClose: handleCloseDialogRenovateIntegration,
          },
          delete: {
            open: openDialogConfirmDeleteIntegration,
            data: selectedIntegrationDelete,
            handleOpen: handleOpenDialogConfirmDeleteIntegration,
            handleClose: handleCloseDialogConfirmDeleteIntegration,
          },
          assignWarehouses: {
            open: openDialogIntegrationAssignWarehouses,
            data: selectedCompanyIntegrationIdToAssignWarehouses,
            handleOpen: handleOpenDialogIntegrationAssignWarehouses,
            handleClose: handleCloseDialogIntegrationAssignWarehouses,
          },
          assignBillingList: {
            open: openDialogAssignBillingList,
            data: selectedCompanyIntegrationToAssignBillingList,
            handleOpen: handleOpenDialogAssignBillingList,
            handleClose: handleCloseDialogAssignBillingList,
          },
        },
        hiddenTableEmpty,
      }}
    >
      {children}
    </IntegrationsSectionContext.Provider>
  );
};

export default IntegrationsSectionProvider;
